/**
 * PromptMySite Widget - Global API
 *
 * Exposes the global window.PromptMySite API for:
 * - Programmatic initialization
 * - Widget control (open/close/toggle)
 * - Workspace management
 * - Custom block registration
 * - List store access
 */

import { mergeConfig, type WidgetConfig } from '../core/config';
import { ApiClient, type WorkspaceSummary } from '../core/api-client';
import { debug, warn } from '../core/logger';
import { getListStore, resetListStore, type SharedListStore, type ListItem } from '../core/list-store';
import type { CartResult } from '../core/cart-integration';
import { PromptMySiteWidget } from './widget';
import { createLoaderByName, createActivityLoader } from '../core/loading/activity-loader';
import { setBlockRendererContext } from '../ui/blocks';

// =============================================================================
// TYPES
// =============================================================================

export interface PromptMySiteApi {
  // Programmatic init
  init: (config: Partial<WidgetConfig>) => Promise<void>;
  // Instance methods
  open: () => void;
  close: () => void;
  toggle: () => void;
  destroy: () => void;
  getConfig: () => WidgetConfig;
  switchWorkspace: (workspaceId: string) => Promise<void>;
  listWorkspaces: () => Promise<WorkspaceSummary[]>;
  // Mode switching
  switchToImmersiveSplit: () => void;
  switchToOverlay: () => void;
  switchToBubble: () => void;
  // Static methods
  registerBlock: typeof PromptMySiteWidget.registerBlock;
  getBlockTypes: typeof PromptMySiteWidget.getBlockTypes;
  // Block registry access for demos/debugging
  blockRegistry: typeof PromptMySiteWidget.blockRegistry;
  // Loader factory for demos
  createLoaderByName: typeof createLoaderByName;
  createActivityLoader: typeof createActivityLoader;
  // List store access for split panel feature
  getListStore: () => SharedListStore;
  resetListStore: () => void;
  // Block renderer context for demos
  setBlockRendererContext: typeof setBlockRendererContext;
  // Cart integration — host site registers checkout handler
  onCheckout: ((items: ListItem[]) => void | Promise<void | CartResult>) | null;
}

// =============================================================================
// STATE
// =============================================================================

let activeWidget: PromptMySiteWidget | null = null;

export function getActiveWidget(): PromptMySiteWidget | null {
  return activeWidget;
}

export function setActiveWidget(widget: PromptMySiteWidget | null): void {
  activeWidget = widget;
}

// =============================================================================
// PROGRAMMATIC INITIALIZATION
// =============================================================================

export async function programmaticInit(userConfig: Partial<WidgetConfig>): Promise<void> {
  if (!userConfig.workspaceId) {
    throw new Error('[PromptMySite] workspaceId is required');
  }

  // Destroy existing widget if any
  if (activeWidget) {
    activeWidget.destroy();
    activeWidget = null;
  }

  const config = mergeConfig(userConfig);
  activeWidget = new PromptMySiteWidget(config);

  // Update global API to point to new widget
  updateGlobalApiWithWidget(activeWidget);

  await activeWidget.init();
  debug(' Widget ready (programmatic init)');
}

// =============================================================================
// API BINDING
// =============================================================================

export function updateGlobalApiWithWidget(widget: PromptMySiteWidget): void {
  const api = (window as unknown as { PromptMySite: PromptMySiteApi }).PromptMySite;
  if (api) {
    api.open = () => widget.open();
    api.close = () => widget.close();
    api.toggle = () => widget.toggle();
    api.destroy = () => widget.destroy();
    api.getConfig = () => widget.getConfig();
    api.switchWorkspace = (workspaceId: string) => widget.switchWorkspace(workspaceId);
    api.listWorkspaces = () => widget.listWorkspaces();
    api.switchToImmersiveSplit = () => widget.switchToImmersiveSplit();
    api.switchToOverlay = () => widget.switchToOverlay();
    api.switchToBubble = () => widget.switchToBubble();
  }
}

// =============================================================================
// MINIMAL API CLIENT
// =============================================================================

function createMinimalApi(): ApiClient {
  let apiUrl = 'https://api.promptmysite.com';
  const scripts = document.getElementsByTagName('script');
  for (let i = 0; i < scripts.length; i++) {
    const script = scripts[i];
    if (script && script.hasAttribute('data-api-url')) {
      apiUrl = script.getAttribute('data-api-url') || apiUrl;
      break;
    }
  }
  return new ApiClient(apiUrl);
}

// =============================================================================
// GLOBAL API SETUP
// =============================================================================

const minimalApiClient = createMinimalApi();

export const globalApi: PromptMySiteApi = {
  // Programmatic init
  init: programmaticInit,
  // Instance methods - updated when widget is created
  open: () => { warn('Widget not initialized. Call init() first.'); },
  close: () => { warn('Widget not initialized. Call init() first.'); },
  toggle: () => { warn('Widget not initialized. Call init() first.'); },
  destroy: () => { warn('Widget not initialized. Call init() first.'); },
  getConfig: () => { warn('Widget not initialized. Call init() first.'); return {} as WidgetConfig; },
  switchWorkspace: async () => { warn('Widget not initialized. Call init() first.'); },
  listWorkspaces: () => minimalApiClient.listWorkspaces(),
  // Mode switching
  switchToImmersiveSplit: () => { warn('Widget not initialized. Call init() first.'); },
  switchToOverlay: () => { warn('Widget not initialized. Call init() first.'); },
  switchToBubble: () => { warn('Widget not initialized. Call init() first.'); },
  // Static methods
  registerBlock: PromptMySiteWidget.registerBlock,
  getBlockTypes: PromptMySiteWidget.getBlockTypes,
  // Block registry access for demos/debugging
  blockRegistry: PromptMySiteWidget.blockRegistry,
  // Loader factory for demos
  createLoaderByName,
  createActivityLoader,
  // List store access
  getListStore,
  resetListStore,
  // Block renderer context for demos
  setBlockRendererContext,
  // Cart integration
  onCheckout: null,
};

export function exposeGlobalApi(): void {
  (window as unknown as { PromptMySite: PromptMySiteApi }).PromptMySite = globalApi;
}
